/**
 * Box Section with GSAP ScrollTrigger
 * Handles 3-state transitions on desktop with pinned scrolling
 * Mobile: Reviews slider with drag functionality
 */

"use strict";

function initBoxMobileVideo() {
  const mobileSection = document.querySelector(
    ".box-state-section.state-one-mobile"
  );
  if (!mobileSection) return;

  const isMobile = window.innerWidth <= 999;
  if (!isMobile) return;

  const video = mobileSection.querySelector(".trimmer-video-mobile");
  if (!video) return;

  // Set initial video state
  video.currentTime = 0;
  video.pause();

  let ticking = false;
  let scrollListenerActive = false;

  function updateVideoProgress() {
    if (!video || video.readyState < 2) return; // Wait for metadata

    const rect = mobileSection.getBoundingClientRect();
    const scrollTop = window.scrollY || window.pageYOffset;
    const windowHeight = window.innerHeight;

    // Get section's position relative to document
    const sectionTop = scrollTop + rect.top;
    const sectionHeight = rect.height;

    // Calculate scroll positions:
    // scrollStart: when section top hits viewport bottom (rect.top === windowHeight)
    //   At this point: scrollTop = sectionTop - windowHeight
    // scrollEnd: when section bottom hits viewport bottom (rect.bottom === windowHeight)
    //   At this point: rect.top = windowHeight - sectionHeight
    //   So: scrollTop = sectionTop - (windowHeight - sectionHeight) = sectionTop - windowHeight + sectionHeight
    const scrollStart = sectionTop - windowHeight / 2; // When section top is at viewport bottom
    const scrollEnd = sectionTop - windowHeight + sectionHeight; // When section bottom is at viewport bottom
    const scrollRange = scrollEnd - scrollStart;

    // Current scroll position
    const currentScroll = scrollTop;

    if (currentScroll < scrollStart) {
      // Before scroll range - video at start
      video.currentTime = 0;
      video.pause();
    } else if (currentScroll > scrollEnd) {
      // After scroll range - video at end
      if (video.duration) {
        video.currentTime = video.duration;
        video.pause();
      }
    } else {
      // Within scroll range - map scroll progress to video time
      const scrollProgress = (currentScroll - scrollStart) / scrollRange;
      const videoProgress = Math.max(0, Math.min(1, scrollProgress));

      if (video.duration) {
        video.currentTime = videoProgress * video.duration;
      }
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateVideoProgress();
      ticking = false;
    });
  }

  function attachScrollListener() {
    if (!scrollListenerActive) {
      window.addEventListener("scroll", onScroll, { passive: true });
      scrollListenerActive = true;
      updateVideoProgress();
    }
  }

  function detachScrollListener() {
    if (scrollListenerActive) {
      window.removeEventListener("scroll", onScroll);
      scrollListenerActive = false;
      if (video) {
        video.currentTime = 0;
        video.pause();
      }
    }
  }

  // Wait for video metadata to load
  video.addEventListener("loadedmetadata", () => {
    video.currentTime = 0;
    video.pause();
    updateVideoProgress();
  });

  // Use Intersection Observer to attach/detach scroll listener
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          attachScrollListener();
        } else {
          detachScrollListener();
        }
      });
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    }
  );

  observer.observe(mobileSection);

  // Also handle resize
  window.addEventListener(
    "resize",
    () => {
      if (scrollListenerActive) {
        updateVideoProgress();
      }
    },
    { passive: true }
  );
}

function initBoxSection() {
  const section = document.querySelector('.box-desktop[id^="box-section-"]');
  if (!section) return;

  const isMobile = window.innerWidth <= 999;

  // On mobile, don't initialize scroll trigger
  if (isMobile) {
    return;
  }

  // Get all elements
  const boxWrapper = section.querySelector(".box-wrapper[data-state='1-2-3']");
  const state1 = section.querySelector(".box-state[data-state-index='1']");
  const state2 = section.querySelector(".box-state[data-state-index='2']");
  const state3 = section.querySelector(".box-state[data-state-index='3']");

  // Middle box images
  const middleImages = {
    leftTop: section.querySelector(".box-left-top"),
    leftBottom: section.querySelector(".box-left-bottom"),
    rightTop: section.querySelector(".box-right-top"),
    rightBottom: section.querySelector(".box-right-bottom"),
  };

  // Left box images (state 1)
  const leftImages = section.querySelectorAll(".box-desktop-wrapper img");

  // Reviews slider elements
  const reviewsContainer = section.querySelector(".box-reviews");
  const reviewsTrack = section.querySelector(".box-reviews-track");
  const reviews = section.querySelectorAll(".box-review");
  const bullets = section.querySelectorAll(".box-bullet");

  // FAQ drag elements
  const faqContainer = section.querySelector(".box-faq-container");
  const faqTrack = section.querySelector(".box-faq-track");
  const faqMask = section.querySelector(".box-faq-mask");

  // Wait for GSAP to be available
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    setTimeout(initBoxSection, 100);
    return;
  }

  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Initialize reviews slider with custom drag
  let reviewsSliderInitialized = false;
  const totalReviews = reviews.length;
  const reviewsPerView = 2;
  const totalSlides =
    totalReviews > reviewsPerView ? totalReviews - reviewsPerView + 1 : 1; // For 3 reviews, 2 slides

  function initReviewsSlider() {
    if (!reviewsContainer || reviews.length === 0) return;
    if (reviewsSliderInitialized) return;

    // Set initial position
    reviewsTrack.style.transform = "translateX(0)";
    reviewsTrack.style.transition = "transform 0.3s ease";

    // Calculate slide width (2 reviews width + gap)
    // Each slide shows 2 reviews, so we move by the width of 2 reviews
    const reviewWidth = reviews[0].offsetWidth;
    const gap = 32; // 2rem gap
    const slideWidth = (reviewWidth + gap) * reviewsPerView; // Width of 2 reviews (100% of visible area)

    let currentPosition = 0;
    let isDragging = false;
    let startX = 0;
    let startPosition = 0;
    const dragThreshold = 30; // Minimum 30px movement to consider it a drag
    const maxPosition = -slideWidth * (totalSlides - 1);

    // Prevent image dragging
    reviews.forEach((review) => {
      const imgs = review.querySelectorAll("img");
      imgs.forEach((img) => {
        img.setAttribute("draggable", "false");
        img.addEventListener("dragstart", (e) => {
          e.preventDefault();
          return false;
        });
      });
    });

    function updateBullets() {
      if (bullets.length === 0) return;
      const slideIndex = Math.round(Math.abs(currentPosition) / slideWidth);
      bullets.forEach((bullet, index) => {
        if (index === slideIndex) {
          bullet.style.opacity = "1";
          bullet.style.transform = "scale(1.2)";
          bullet.classList.add("active");
        } else {
          bullet.style.opacity = "0.5";
          bullet.style.transform = "scale(1)";
          bullet.classList.remove("active");
        }
      });
    }

    function goToSlide(slideIndex) {
      const clampedSlide = Math.max(0, Math.min(slideIndex, totalSlides - 1));
      const targetX = -clampedSlide * slideWidth;
      slideTo(targetX);
    }

    function slideTo(position) {
      currentPosition = position;
      reviewsTrack.style.transition = "transform 0.3s ease";
      reviewsTrack.style.transform = `translateX(${position}px)`;
      updateBullets();
    }

    function startDrag(e) {
      if (e.type === "mousedown" && e.button !== 0) return;
      isDragging = false;
      startX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
      startPosition = currentPosition;
      document.addEventListener("mousemove", handleDrag);
      document.addEventListener("touchmove", handleDrag, { passive: false });
      document.addEventListener("mouseup", endDrag);
      document.addEventListener("touchend", endDrag);
      e.preventDefault();
    }

    function handleDrag(e) {
      const currentX =
        e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
      const diff = startX - currentX;
      if (!isDragging && Math.abs(diff) < dragThreshold) return;
      if (!isDragging) {
        isDragging = true;
        reviewsTrack.style.transition = "none";
      }
      currentPosition = Math.max(
        Math.min(startPosition - diff, 0),
        maxPosition
      );
      reviewsTrack.style.transform = `translateX(${currentPosition}px)`;
      e.preventDefault();
    }

    function endDrag(e) {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("touchmove", handleDrag);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchend", endDrag);

      const endX =
        e.type === "touchend"
          ? e.changedTouches
            ? e.changedTouches[0].clientX
            : startX
          : e.clientX;
      const totalDiff = Math.abs(startX - endX);

      if (!isDragging || totalDiff < dragThreshold) {
        // If movement was less than 30px, treat as click
        const target = e.target.closest("a");
        if (target) target.click();
        // Reset position if it wasn't a proper drag
        if (!isDragging) {
          slideTo(startPosition);
        }
        return;
      }

      isDragging = false;

      // Calculate starting slide index
      const startSlideIndex = Math.round(Math.abs(startPosition) / slideWidth);

      // Calculate drag direction: positive diff = drag left (next), negative diff = drag right (prev)
      const dragDistance = startX - endX;

      // Determine target slide: increment or decrement by 1 based on direction
      let targetSlideIndex = startSlideIndex;
      if (dragDistance > 0) {
        // Dragged left, go to next slide
        targetSlideIndex = startSlideIndex + 1;
      } else if (dragDistance < 0) {
        // Dragged right, go to previous slide
        targetSlideIndex = startSlideIndex - 1;
      }

      // Clamp to valid range
      const clampedSlide = Math.max(
        0,
        Math.min(targetSlideIndex, totalSlides - 1)
      );
      const targetX = -clampedSlide * slideWidth;
      slideTo(targetX);
    }

    // Add event listeners for dragging
    reviewsTrack.addEventListener("mousedown", startDrag);
    reviewsTrack.addEventListener("touchstart", startDrag, { passive: false });

    // Make bullets clickable
    bullets.forEach((bullet, index) => {
      bullet.addEventListener("click", () => {
        goToSlide(index);
      });
    });

    // Initialize bullets
    updateBullets();
    reviewsSliderInitialized = true;
  }

  // Initialize FAQ vertical drag
  let faqDragInitialized = false;

  function initFaqDrag() {
    if (!faqContainer || !faqTrack) return;
    if (faqDragInitialized) return;

    // Set initial position
    faqTrack.style.transform = "translateY(0)";
    faqTrack.style.transition = "transform 0.3s ease";

    let currentPosition = 0;
    let isDragging = false;
    let wasDragging = false;
    let startY = 0;
    let startPosition = 0;
    const dragThreshold = 8; // 8px threshold to differentiate click from drag

    function updateFaqMask() {
      if (!faqMask) return;

      const containerHeight = faqContainer.offsetHeight;
      const trackHeight = faqTrack.scrollHeight;

      // Only show mask if content exceeds container height
      if (trackHeight <= containerHeight) {
        faqMask.classList.remove("visible");
        return;
      }

      const maxPosition = Math.min(0, containerHeight - trackHeight);
      const distanceFromEnd = Math.abs(currentPosition - maxPosition);

      // Hide mask when close to the end (within 10px)
      if (distanceFromEnd < 10) {
        faqMask.classList.remove("visible");
      } else {
        faqMask.classList.add("visible");
      }
    }

    function updateFaqPosition() {
      const containerHeight = faqContainer.offsetHeight;
      const trackHeight = faqTrack.scrollHeight;
      const maxPosition = Math.min(0, containerHeight - trackHeight);

      // Clamp position to valid range
      currentPosition = Math.max(maxPosition, Math.min(currentPosition, 0));
      faqTrack.style.transition = "transform 0.3s ease";
      faqTrack.style.transform = `translateY(${currentPosition}px)`;

      // Update mask visibility
      updateFaqMask();
    }

    // Initialize mask visibility
    updateFaqMask();

    function startDrag(e) {
      if (e.type === "mousedown" && e.button !== 0) return;

      // Only allow dragging if content overflows
      const containerHeight = faqContainer.offsetHeight;
      const trackHeight = faqTrack.scrollHeight;
      if (trackHeight <= containerHeight) return; // No dragging needed if content fits

      isDragging = false;
      startY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
      startPosition = currentPosition;

      document.addEventListener("mousemove", handleDrag);
      document.addEventListener("touchmove", handleDrag, { passive: false });
      document.addEventListener("mouseup", endDrag);
      document.addEventListener("touchend", endDrag);

      e.preventDefault();
    }

    function handleDrag(e) {
      const currentY =
        e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
      const diff = startY - currentY;

      // Check if we've exceeded the threshold
      if (!isDragging && Math.abs(diff) < dragThreshold) return;

      if (!isDragging) {
        isDragging = true;
        faqTrack.style.transition = "none";
      }

      const containerHeight = faqContainer.offsetHeight;
      const trackHeight = faqTrack.scrollHeight;
      const maxPosition = Math.min(0, containerHeight - trackHeight);

      // Update position (negative diff means dragging up, so move track up)
      currentPosition = Math.max(
        maxPosition,
        Math.min(startPosition - diff, 0)
      );
      faqTrack.style.transform = `translateY(${currentPosition}px)`;

      // Update mask visibility during drag
      updateFaqMask();
      e.preventDefault();
    }

    function endDrag(e) {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("touchmove", handleDrag);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchend", endDrag);

      const endY =
        e.type === "touchend"
          ? e.changedTouches
            ? e.changedTouches[0].clientY
            : startY
          : e.clientY;
      const totalDiff = Math.abs(startY - endY);

      wasDragging = isDragging && totalDiff >= dragThreshold;

      if (!wasDragging) {
        // Was a click, allow default behavior (FAQ accordion click)
        isDragging = false;
        return;
      }

      isDragging = false;
      updateFaqPosition();

      // Prevent click events after drag
      setTimeout(() => {
        wasDragging = false;
      }, 100);
    }

    // Add event listeners for dragging
    faqTrack.addEventListener("mousedown", startDrag);
    faqTrack.addEventListener("touchstart", startDrag, { passive: false });

    // Prevent clicks when dragging
    faqTrack.addEventListener(
      "click",
      function (e) {
        if (wasDragging) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      },
      true
    );

    faqDragInitialized = true;
  }

  // Set initial state (State 1)
  function setInitialState() {
    // Box states: show state 1, hide others
    if (state1) {
      gsap.set(state1, { opacity: 1, visibility: "visible" });
    }
    if (state2) {
      gsap.set(state2, { opacity: 0, visibility: "hidden" });
    }
    if (state3) {
      gsap.set(state3, { opacity: 0, visibility: "hidden" });
    }

    // Left images: visible and set initial transform
    const leftImagesWrapper = section.querySelector(".box-desktop-images");
    const leftWrapperWidth = leftImagesWrapper
      ? leftImagesWrapper.offsetWidth
      : 400;

    leftImages.forEach((img, index) => {
      const wrapper = img.parentElement;
      gsap.set(wrapper, {
        opacity: 1,
        visibility: "visible",
        x: 0, // Start at position 0
      });
    });

    // Middle images: hidden and positioned at left edge of parent container
    // All start at the same position (left edge of .box-middle), then move to their final positions
    const leftTop = middleImages.leftTop;
    const leftBottom = middleImages.leftBottom;
    const rightTop = middleImages.rightTop;
    const rightBottom = middleImages.rightBottom;

    // Calculate positions: all images start at left center of parent, then move to their CSS positions
    const boxMiddle = section.querySelector(".box-middle");
    const containerHeight = boxMiddle ? boxMiddle.offsetHeight : 800;
    const containerWidth = boxMiddle ? boxMiddle.offsetWidth : 400;

    // Calculate left center position (x: 0, y: center of container)
    // Since images are absolutely positioned, we need to calculate their offset from their CSS positions
    const centerY = containerHeight / 2;

    // Calculate positions: all images start at left center but hidden outside left (translateX: -100%)
    // Get element widths to calculate -100% translate
    const leftTopWidth = leftTop ? leftTop.offsetWidth : 0;
    const leftBottomWidth = leftBottom ? leftBottom.offsetWidth : 0;
    const rightTopWidth = rightTop ? rightTop.offsetWidth : 0;
    const rightBottomWidth = rightBottom ? rightBottom.offsetWidth : 0;

    // Calculate offsets to align all at left center initially
    let leftTopCenterX = 0;
    let leftTopCenterY = 0;
    let leftBottomCenterX = 0;
    let leftBottomCenterY = 0;
    let rightTopCenterX = 0;
    let rightTopCenterY = 0;
    let rightBottomCenterX = 0;
    let rightBottomCenterY = 0;

    if (leftTop) {
      const computedStyle = window.getComputedStyle(leftTop);
      const leftValue = parseFloat(computedStyle.left) || 0;
      const elementHeight = leftTop.offsetHeight || 0;
      const elementTop =
        leftTop.getBoundingClientRect().top -
        (boxMiddle ? boxMiddle.getBoundingClientRect().top : 0);
      leftTopCenterX = -leftValue; // Left center position (x: 0 relative to parent)
      leftTopCenterY = centerY - (elementTop + elementHeight / 2); // Vertical center
    }
    if (leftBottom) {
      const computedStyle = window.getComputedStyle(leftBottom);
      const leftValue = parseFloat(computedStyle.left) || 0;
      const elementHeight = leftBottom.offsetHeight || 0;
      const elementTop =
        leftBottom.getBoundingClientRect().top -
        (boxMiddle ? boxMiddle.getBoundingClientRect().top : 0);
      leftBottomCenterX = -leftValue; // Left center position
      leftBottomCenterY = centerY - (elementTop + elementHeight / 2); // Vertical center
    }
    if (rightTop) {
      const computedStyle = window.getComputedStyle(rightTop);
      const rightValue = parseFloat(computedStyle.right) || 0;
      const elementHeight = rightTop.offsetHeight || 0;
      const elementTop =
        rightTop.getBoundingClientRect().top -
        (boxMiddle ? boxMiddle.getBoundingClientRect().top : 0);
      rightTopCenterX = -(containerWidth - rightTopWidth - rightValue); // Left center position
      rightTopCenterY = centerY - (elementTop + elementHeight / 2); // Vertical center
    }
    if (rightBottom) {
      const computedStyle = window.getComputedStyle(rightBottom);
      const rightValue = parseFloat(computedStyle.right) || 0;
      const elementHeight = rightBottom.offsetHeight || 0;
      const elementTop =
        rightBottom.getBoundingClientRect().top -
        (boxMiddle ? boxMiddle.getBoundingClientRect().top : 0);
      rightBottomCenterX = -(containerWidth - rightBottomWidth - rightValue); // Left center position
      rightBottomCenterY = centerY - (elementTop + elementHeight / 2); // Vertical center
    }

    // Set middle images to start at left center but hidden outside left (translateX: -100%)
    if (leftTop) {
      gsap.set(leftTop, {
        opacity: 0,
        visibility: "hidden",
        x: leftTopCenterX - leftTopWidth, // Left center position + translateX -100% (hidden outside left)
        y: leftTopCenterY,
      });
    }
    if (leftBottom) {
      gsap.set(leftBottom, {
        opacity: 0,
        visibility: "hidden",
        x: leftBottomCenterX - leftBottomWidth, // Left center position + translateX -100%
        y: leftBottomCenterY,
      });
    }
    if (rightTop) {
      gsap.set(rightTop, {
        opacity: 0,
        visibility: "hidden",
        x: rightTopCenterX - rightTopWidth, // Left center position + translateX -100%
        y: rightTopCenterY,
      });
    }
    if (rightBottom) {
      gsap.set(rightBottom, {
        opacity: 0,
        visibility: "hidden",
        x: rightBottomCenterX - rightBottomWidth, // Left center position + translateX -100%
        y: rightBottomCenterY,
      });
    }

    // Store values for later use
    window.leftWrapperWidth = leftWrapperWidth;
    window.middleImageStartPositions = [
      { x: leftTopCenterX - leftTopWidth, y: leftTopCenterY }, // Hidden outside left
      { x: leftBottomCenterX - leftBottomWidth, y: leftBottomCenterY },
      { x: rightTopCenterX - rightTopWidth, y: rightTopCenterY },
      { x: rightBottomCenterX - rightBottomWidth, y: rightBottomCenterY },
    ];
    window.middleImageCenterPositions = [
      { x: leftTopCenterX, y: leftTopCenterY }, // Left center visible
      { x: leftBottomCenterX, y: leftBottomCenterY },
      { x: rightTopCenterX, y: rightTopCenterY },
      { x: rightBottomCenterX, y: rightBottomCenterY },
    ];
    window.middleImageFinalPositions = [
      { x: 0, y: 0 }, // Final position (CSS handles it)
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ];
  }

  // Initialize state 1
  setInitialState();

  // Create progress bar for desktop (3 states)
  let progressBar = null;
  if (typeof window.createSectionProgressBar === "function") {
    progressBar = window.createSectionProgressBar(section, 3);
  }

  // Create ScrollTrigger for state transitions
  ScrollTrigger.create({
    trigger: section,
    start: "top top",
    end: "+=300%", // 3x viewport height for 3 states
    pin: true,
    anticipatePin: 1,
    onUpdate: (self) => {
      const progress = self.progress;

      // Define transition points for 3 states
      const state1End = 0.33; // State 1 → State 2 transition ends at 33%
      const state2HoldStart = 0.33; // State 2 hold period starts at 33%
      const state2HoldEnd = 0.5; // State 2 hold period ends at 50%
      const state3TransitionStart = 0.5; // State 2 → State 3 transition starts at 50%
      const state3End = 0.83; // State 2 → State 3 transition ends at 83%

      // Calculate values for each state
      let state1Opacity, state2Opacity, state3Opacity;
      let leftImagesOpacity, middleImagesOpacity;

      if (progress <= state1End) {
        // State 1 → State 2: Fade out state 1, fade in state 2
        const state1Progress = progress / state1End;

        state1Opacity = 1 - state1Progress;
        state2Opacity = state1Progress;
        state3Opacity = 0;

        // Left images fade out, middle images fade in
        leftImagesOpacity = 1 - state1Progress;
        middleImagesOpacity = state1Progress;
      } else if (progress <= state2HoldEnd) {
        // State 2: Hold period
        state1Opacity = 0;
        state2Opacity = 1;
        state3Opacity = 0;
        leftImagesOpacity = 0;
        middleImagesOpacity = 1;
      } else if (progress <= state3End) {
        // State 2 → State 3: Fade out state 2, fade in state 3
        const state2Progress =
          (progress - state2HoldEnd) / (state3End - state2HoldEnd);

        state1Opacity = 0;
        state2Opacity = 1 - state2Progress;
        state3Opacity = state2Progress;
        leftImagesOpacity = 0;
        middleImagesOpacity = 1; // Keep middle images visible
      } else {
        // State 3: Final state
        state1Opacity = 0;
        state2Opacity = 0;
        state3Opacity = 1;
        leftImagesOpacity = 0;
        middleImagesOpacity = 1;
      }

      // Update box states
      if (state1) {
        gsap.to(state1, {
          opacity: state1Opacity,
          visibility: state1Opacity > 0 ? "visible" : "hidden",
          duration: 0.1,
          ease: "none",
        });
      }
      if (state2) {
        gsap.to(state2, {
          opacity: state2Opacity,
          visibility: state2Opacity > 0 ? "visible" : "hidden",
          duration: 0.1,
          ease: "none",
          onComplete: () => {
            // Initialize slider when state 2 becomes visible
            if (state2Opacity > 0) {
              initReviewsSlider();
            }
          },
        });
      }
      if (state3) {
        gsap.to(state3, {
          opacity: state3Opacity,
          visibility: state3Opacity > 0 ? "visible" : "hidden",
          duration: 0.1,
          ease: "none",
          onComplete: () => {
            // Initialize FAQ drag when state 3 becomes visible
            if (state3Opacity > 0) {
              // Small delay to ensure DOM is ready
              setTimeout(() => {
                initFaqDrag();
              }, 100);
            }
          },
        });
      }

      // Update left images - translate out 100% with stagger
      const leftWrapperWidth = window.leftWrapperWidth || 400;
      const staggerDelay = 0.1; // Delay between each image

      leftImages.forEach((img, index) => {
        const wrapper = img.parentElement;
        if (progress <= state1End) {
          const state1Progress = progress / state1End;
          // Phase 1: Translate out (first 40% of progress)
          const translateProgress = Math.min(state1Progress / 0.4, 1);
          const staggerProgress = Math.max(
            0,
            (state1Progress - index * staggerDelay) / 0.4
          );
          const clampedStagger = Math.max(0, Math.min(staggerProgress, 1));

          gsap.to(wrapper, {
            opacity: leftImagesOpacity * (1 - clampedStagger),
            visibility: clampedStagger < 1 ? "visible" : "hidden",
            x: leftWrapperWidth * clampedStagger, // Translate 100% to the right
            duration: 0.1,
            ease: "none",
          });
        } else {
          // After state 1, keep them hidden and translated
          gsap.to(wrapper, {
            opacity: 0,
            visibility: "hidden",
            x: leftWrapperWidth,
            duration: 0.1,
            ease: "none",
          });
        }
      });

      // Update middle images with position animations
      const leftTop = middleImages.leftTop;
      const leftBottom = middleImages.leftBottom;
      const rightTop = middleImages.rightTop;
      const rightBottom = middleImages.rightBottom;

      if (progress <= state1End) {
        // State 1 → State 2: Staggered animation sequence
        const state1Progress = progress / state1End;
        const startPositions = window.middleImageStartPositions || [
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
        ];
        const centerPositions = window.middleImageCenterPositions || [
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
        ];
        const finalPositions = window.middleImageFinalPositions || [
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
          { x: 0, y: 0 },
        ];
        const staggerDelay = 0.1;

        // Phase 1 (0-40%): Left images translate out
        // Phase 2 (40-60%): Middle images move from hidden outside left to left center visible
        // Phase 3 (60-100%): Middle images move from left center to final positions

        const moveToCenterStart = 0.4;
        const moveToFinalStart = 0.6;

        const middleImageArray = [leftTop, leftBottom, rightTop, rightBottom];

        middleImageArray.forEach((img, index) => {
          if (!img) return;

          let opacity = 0;
          let visibility = "hidden";
          const startPos = startPositions[index] || { x: 0, y: 0 };
          const centerPos = centerPositions[index] || { x: 0, y: 0 };
          let x = startPos.x; // Start hidden outside left
          let y = startPos.y;

          if (state1Progress >= moveToCenterStart) {
            // Phase 2: Move from hidden outside left to left center visible (staggered)
            const moveToCenterProgress =
              (state1Progress - moveToCenterStart) /
              (moveToFinalStart - moveToCenterStart);
            const staggerProgress = Math.max(
              0,
              (moveToCenterProgress - index * staggerDelay) /
                (1 - index * staggerDelay)
            );
            const clampedMove = Math.max(0, Math.min(staggerProgress, 1));

            // Interpolate from start (hidden outside) to center (visible)
            x = startPos.x * (1 - clampedMove) + centerPos.x * clampedMove;
            y = startPos.y * (1 - clampedMove) + centerPos.y * clampedMove;

            // Fade in as they move to center
            opacity = clampedMove;
            visibility = clampedMove > 0 ? "visible" : "hidden";
          }

          if (state1Progress >= moveToFinalStart) {
            // Phase 3: Move from left center to final position (staggered)
            const moveToFinalProgress =
              (state1Progress - moveToFinalStart) / (1 - moveToFinalStart);
            const staggerProgress = Math.max(
              0,
              (moveToFinalProgress - index * staggerDelay) /
                (1 - index * staggerDelay)
            );
            const clampedMove = Math.max(0, Math.min(staggerProgress, 1));

            opacity = 1;
            visibility = "visible";
            // Interpolate from center position to final position
            x =
              centerPos.x * (1 - clampedMove) +
              finalPositions[index].x * clampedMove;
            y =
              centerPos.y * (1 - clampedMove) +
              finalPositions[index].y * clampedMove;
          }

          gsap.to(img, {
            opacity: opacity,
            visibility: visibility,
            x: x,
            y: y,
            duration: 0.1,
            ease: "none",
          });
        });
      } else if (progress <= state2HoldEnd) {
        // State 2: Hold at original positions
        if (leftTop) {
          gsap.to(leftTop, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
        if (leftBottom) {
          gsap.to(leftBottom, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
        if (rightTop) {
          gsap.to(rightTop, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
        if (rightBottom) {
          gsap.to(rightBottom, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
      } else {
        // State 3: Keep at state 2 positions (no swap)
        if (leftTop) {
          gsap.to(leftTop, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
        if (leftBottom) {
          gsap.to(leftBottom, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
        if (rightTop) {
          gsap.to(rightTop, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
        if (rightBottom) {
          gsap.to(rightBottom, {
            opacity: middleImagesOpacity,
            visibility: middleImagesOpacity > 0 ? "visible" : "hidden",
            x: 0,
            y: 0,
            duration: 0.1,
            ease: "none",
          });
        }
      }

      // Update progress bar
      if (progressBar) {
        // Determine current state based on progress
        let currentState = 1;
        if (progress >= state3End) {
          currentState = 3;
        } else if (progress >= state1End) {
          currentState = 2;
        } else {
          currentState = 1;
        }
        progressBar.updateProgress(progress, currentState);
      }
    },
  });

  // Try to initialize slider after a delay (fallback)
  setTimeout(() => {
    if (state2 && state2.style.opacity !== "0") {
      initReviewsSlider();
    }
    if (state3 && state3.style.opacity !== "0") {
      initFaqDrag();
    }
  }, 1000);

  // Handle window resize
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      ScrollTrigger.refresh();
      if (window.innerWidth <= 999) {
        // Mobile: reset states
        if (state1) state1.style.opacity = "1";
        if (state2) state2.style.opacity = "1";
        if (state3) state3.style.opacity = "1";
        leftImages.forEach((img) => {
          img.parentElement.style.opacity = "1";
        });
        Object.values(middleImages).forEach((imgWrapper) => {
          if (imgWrapper) imgWrapper.style.opacity = "1";
        });
        // Mobile: reinitialize video
        initBoxMobileVideo();
      } else {
        // Desktop: reinitialize
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
        reviewsSliderInitialized = false;
        faqDragInitialized = false;
        initBoxSection();
      }
    }, 250);
  });
}

// Initialize when DOM is ready
function initialize() {
  // Use global scroll-to-top function if available, otherwise execute immediately
  if (typeof window.ensureScrollToTop === "function") {
    window.ensureScrollToTop(() => {
      initBoxSection();
      initBoxMobileVideo();
    });
  } else {
    // Fallback: wait a bit for global.js to load
    setTimeout(() => {
      if (typeof window.ensureScrollToTop === "function") {
        window.ensureScrollToTop(() => {
          initBoxSection();
          initBoxMobileVideo();
        });
      } else {
        initBoxSection();
        initBoxMobileVideo();
      }
    }, 100);
  }
}

// Wait for DOM to be ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initialize);
} else {
  initialize();
}

// Initialize mobile reviews slider
function initMobileReviewsSlider() {
  const isMobile = window.matchMedia("(max-width:999px)").matches;
  const mobileReviewsContainer = document.querySelector(".box-reviews-mobile");
  if (!mobileReviewsContainer) return;

  const reviewsTrack = mobileReviewsContainer.querySelector(
    ".box-reviews-mobile-track"
  );
  const reviews = mobileReviewsContainer.querySelectorAll(".box-review-mobile");
  const bullets = mobileReviewsContainer.querySelectorAll(".box-bullet-mobile");

  if (reviews.length === 0) return;

  const reviewsPerView = 2;
  const totalReviews = reviews.length;
  const totalSlides =
    totalReviews > reviewsPerView ? totalReviews - reviewsPerView + 1 : 1;

  // Set initial position
  reviewsTrack.style.transform = "translateX(0)";
  reviewsTrack.style.transition = "transform 0.3s ease";

  // Calculate slide width (2 reviews width + gap)
  const reviewWidth = reviews[0].offsetWidth;
  const gap = isMobile ? 8 : 16; // 1rem gap
  const slideWidth = (reviewWidth + gap) * reviewsPerView;

  let currentPosition = 0;
  let isDragging = false;
  let startX = 0;
  let startPosition = 0;
  const dragThreshold = 30; // Minimum 30px movement to consider it a drag
  const maxPosition = -slideWidth * (totalSlides - 1);

  // Prevent image dragging
  reviews.forEach((review) => {
    const imgs = review.querySelectorAll("img");
    imgs.forEach((img) => {
      img.setAttribute("draggable", "false");
      img.addEventListener("dragstart", (e) => {
        e.preventDefault();
        return false;
      });
    });
  });

  function updateBullets() {
    if (bullets.length === 0) return;
    const slideIndex = Math.round(Math.abs(currentPosition) / slideWidth);
    bullets.forEach((bullet, index) => {
      if (index === slideIndex) {
        bullet.style.opacity = "1";
        bullet.style.transform = "scale(1.2)";
        bullet.classList.add("active");
      } else {
        bullet.style.opacity = "0.5";
        bullet.style.transform = "scale(1)";
        bullet.classList.remove("active");
      }
    });
  }

  function slideTo(position) {
    currentPosition = position;
    reviewsTrack.style.transition = "transform 0.3s ease";
    reviewsTrack.style.transform = `translateX(${position}px)`;
    updateBullets();
  }

  let startY = 0;

  function startDrag(e) {
    if (e.type === "mousedown" && e.button !== 0) return;
    isDragging = false;
    startX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    startY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    startPosition = currentPosition;
    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("touchmove", handleDrag, { passive: false });
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchend", endDrag);
  }

  function handleDrag(e) {
    const currentX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
    const currentY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
    const diffX = startX - currentX;
    const diffY = Math.abs(startY - currentY);

    // Only prevent default if movement is primarily horizontal
    if (!isDragging) {
      // Check if horizontal movement is greater than vertical movement
      if (Math.abs(diffX) > diffY && Math.abs(diffX) >= dragThreshold) {
        isDragging = true;
        reviewsTrack.style.transition = "none";
        e.preventDefault();
      } else if (diffY > Math.abs(diffX)) {
        // Vertical scrolling detected, don't interfere
        return;
      } else {
        // Not enough movement yet
        return;
      }
    }

    if (isDragging) {
      currentPosition = Math.max(
        Math.min(startPosition - diffX, 0),
        maxPosition
      );
      reviewsTrack.style.transform = `translateX(${currentPosition}px)`;
      e.preventDefault();
    }
  }

  function endDrag(e) {
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("touchmove", handleDrag);
    document.removeEventListener("mouseup", endDrag);
    document.removeEventListener("touchend", endDrag);

    const endX =
      e.type === "touchend"
        ? e.changedTouches
          ? e.changedTouches[0].clientX
          : startX
        : e.clientX;
    const totalDiff = Math.abs(startX - endX);

    if (!isDragging || totalDiff < dragThreshold) {
      // If movement was less than 30px, treat as click
      const target = e.target.closest("a");
      if (target) target.click();
      // Reset position if it wasn't a proper drag
      if (!isDragging) {
        slideTo(startPosition);
      }
      return;
    }

    isDragging = false;

    // Calculate starting slide index
    const startSlideIndex = Math.round(Math.abs(startPosition) / slideWidth);

    // Calculate drag direction: positive diff = drag left (next), negative diff = drag right (prev)
    const dragDistance = startX - endX;

    // Determine target slide: increment or decrement by 1 based on direction
    let targetSlideIndex = startSlideIndex;
    if (dragDistance > 0) {
      // Dragged left, go to next slide
      targetSlideIndex = startSlideIndex + 1;
    } else if (dragDistance < 0) {
      // Dragged right, go to previous slide
      targetSlideIndex = startSlideIndex - 1;
    }

    // Clamp to valid range
    const clampedSlide = Math.max(
      0,
      Math.min(targetSlideIndex, totalSlides - 1)
    );
    const targetX = -clampedSlide * slideWidth;
    slideTo(targetX);
  }

  // Add event listeners
  reviewsTrack.addEventListener("mousedown", startDrag);
  reviewsTrack.addEventListener("touchstart", startDrag, { passive: false });

  // Initialize bullets
  updateBullets();
}

// Handle browser back/forward navigation
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    if (typeof window.ensureScrollToTop === "function") {
      window.ensureScrollToTop(() => {
        const section = document.querySelector(
          '.box-desktop[id^="box-section-"]'
        );
        if (section && typeof ScrollTrigger !== "undefined") {
          ScrollTrigger.getAll().forEach((trigger) => {
            if (trigger.trigger === section) {
              trigger.kill();
            }
          });
        }
        initBoxSection();
        initBoxMobileVideo();
      });
    } else {
      const section = document.querySelector(
        '.box-desktop[id^="box-section-"]'
      );
      if (section && typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
      }
      initBoxSection();
      initBoxMobileVideo();
    }
  }
});

// Initialize mobile slider when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initMobileReviewsSlider();
    initBoxMobileVideo();
  });
} else {
  initMobileReviewsSlider();
  initBoxMobileVideo();
}
